import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../config/theme';
import { useNavigate } from 'react-router-dom';
import PaymentMethodCard from '../PaymentMethodCard';
import { staxConfig } from '../../../../config/stax';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
  max-width: 800px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg};
  }
`;

const Header = styled.div`
  text-align: center;
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.neutral.light};
`;

const Title = styled.h1`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.heading.fontSize};
  font-weight: 600;
  color: ${theme.colors.primary.text};
  margin: 0 0 ${theme.spacing.sm} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.neutral.medium};
  margin: 0;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 400;
  color: ${theme.colors.primary.text};
  margin: 0;
`;

const OrderSummary = styled.div`
  background: ${theme.colors.neutral.lighter};
  border-radius: 8px;
  padding: ${theme.spacing.lg};
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md} 0;
  border-bottom: 1px solid ${theme.colors.neutral.light};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const ItemName = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  font-weight: 500;
  color: ${theme.colors.primary.text};
`;

const ItemDescription = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.875rem;
  color: ${theme.colors.neutral.medium};
`;

const ItemPrice = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  font-weight: 500;
  color: ${theme.colors.primary.text};
`;

const TotalRow = styled(OrderItem)`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.neutral.medium};
  border-bottom: none;
  
  ${ItemName} {
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  ${ItemPrice} {
    font-size: 1.5rem;
    font-weight: 600;
  }
`;

const PaymentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const PaymentMethodSelect = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const NoPaymentMethods = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  background: ${theme.colors.neutral.lighter};
  border-radius: 8px;
`;

const Button = styled.button`
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  
  background: transparent;
  color: ${theme.colors.primary.buttons};
  border: 1px solid ${theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  transition: ${theme.transitions.default};
  text-decoration: none;
  
  &:hover:not(:disabled) {
    background: ${theme.colors.primary.buttons};
    color: ${theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: ${theme.colors.primary.buttons};
  color: ${theme.colors.primary.background};
  
  &:hover:not(:disabled) {
    background: transparent;
    color: ${theme.colors.primary.buttons};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.md};
  z-index: 10;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid ${theme.colors.neutral.light};
  border-top-color: ${theme.colors.primary.buttons};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.primary.text};
  margin: 0;
`;

const ErrorMessage = styled.div`
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid ${theme.colors.accent.danger};
  border-radius: 8px;
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.accent.danger};
  margin: 0;
`;

const RadioButton = styled.input`
  margin-right: ${theme.spacing.sm};
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CheckoutConfirmation = ({
  orderItems = [],
  paymentMethods = [],
  onProcessPayment,
  onCancel,
  isLoading = false,
  error = null
}) => {
  const navigate = useNavigate();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);

  // Set default payment method on mount
  useEffect(() => {
    const defaultMethod = paymentMethods.find(m => m.is_default);
    if (defaultMethod) {
      setSelectedPaymentMethod(defaultMethod.id);
    } else if (paymentMethods.length > 0) {
      setSelectedPaymentMethod(paymentMethods[0].id);
    }
  }, [paymentMethods]);

  // Calculate order total
  const calculateTotal = () => {
    return orderItems.reduce((total, item) => {
      const itemTotal = (item.price || 0) * (item.quantity || 1);
      return total + itemTotal;
    }, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100); // Assuming amount is in cents
  };

  const handleProcessPayment = async () => {
    if (!selectedPaymentMethod || !onProcessPayment) return;

    setIsProcessing(true);
    setProcessingError(null);

    try {
      const paymentData = {
        payment_method_id: selectedPaymentMethod,
        amount: calculateTotal(),
        currency: 'USD',
        items: orderItems,
        metadata: {
          order_type: 'checkout',
          timestamp: new Date().toISOString()
        }
      };

      const result = await onProcessPayment(paymentData);
      
      if (result.success) {
        // Navigate to success page or show success message
        navigate('/checkout/success', { 
          state: { 
            transactionId: result.transaction_id,
            amount: calculateTotal()
          } 
        });
      } else {
        setProcessingError(result.error || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setProcessingError(error.message || staxConfig.errorMessages.generic_error);
    } finally {
      setIsProcessing(false);
    }
  };

  const total = calculateTotal();

  return (
    <Container>
      <Header>
        <Title>Checkout</Title>
        <Subtitle>Review your order and complete payment</Subtitle>
      </Header>

      {(error || processingError) && (
        <ErrorMessage>
          <ErrorText>{error || processingError}</ErrorText>
        </ErrorMessage>
      )}

      <Section>
        <SectionTitle>Order Summary</SectionTitle>
        <OrderSummary>
          {orderItems.map((item, index) => (
            <OrderItem key={item.id || index}>
              <ItemInfo>
                <ItemName>{item.name}</ItemName>
                {item.description && (
                  <ItemDescription>{item.description}</ItemDescription>
                )}
                {item.quantity > 1 && (
                  <ItemDescription>Quantity: {item.quantity}</ItemDescription>
                )}
              </ItemInfo>
              <ItemPrice>
                {formatCurrency(item.price * (item.quantity || 1))}
              </ItemPrice>
            </OrderItem>
          ))}
          
          <TotalRow>
            <ItemInfo>
              <ItemName>Total</ItemName>
            </ItemInfo>
            <ItemPrice>{formatCurrency(total)}</ItemPrice>
          </TotalRow>
        </OrderSummary>
      </Section>

      <Section>
        <SectionTitle>Payment Method</SectionTitle>
        <PaymentSection>
          {paymentMethods.length > 0 ? (
            <PaymentMethodSelect>
              {paymentMethods.map((method) => (
                <RadioLabel key={method.id}>
                  <RadioButton
                    type="radio"
                    name="paymentMethod"
                    value={method.id}
                    checked={selectedPaymentMethod === method.id}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    disabled={isProcessing}
                  />
                  <PaymentMethodCard
                    paymentMethod={method}
                    brandLogos={staxConfig.cardBrandLogos}
                  />
                </RadioLabel>
              ))}
            </PaymentMethodSelect>
          ) : (
            <NoPaymentMethods>
              <p>No payment methods available</p>
              <Button onClick={() => navigate('/account')}>
                Add Payment Method
              </Button>
            </NoPaymentMethods>
          )}
        </PaymentSection>
      </Section>

      <ButtonGroup>
        <Button
          onClick={onCancel || (() => navigate(-1))}
          disabled={isProcessing}
        >
          Back to Cart
        </Button>
        <PrimaryButton
          onClick={handleProcessPayment}
          disabled={!selectedPaymentMethod || isProcessing || isLoading}
        >
          {isProcessing ? 'Processing...' : `Pay ${formatCurrency(total)}`}
        </PrimaryButton>
      </ButtonGroup>

      {isProcessing && (
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>Processing your payment...</LoadingText>
        </LoadingOverlay>
      )}
    </Container>
  );
};

export default CheckoutConfirmation;